<template>
  <div>
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPageLoader"
      :color="loaderColor"
      :blur="''"
    ></loading>
    <div>
      <v-wait for="settings">
        <half-circle-spinner
          slot="waiting"
          style="display: block; margin: 0 auto"
          :animation-duration="1000"
          :size="60"
          color="#3d4852"
        />
        <div class="bg-white rounded mt-3">
          <div class="px-4 py-3 border-solid border-grey-200 border-b">
            <h4 class="font-semibold text-grey-700">
              {{ $t('frontend_funnels_vendor_settings') }}
            </h4>
            <div class="text-sm text-grey-700">
              {{$t('frontend_funnels_you_can_configure')}}
            </div>
          </div>
          <div class="flex">
              <vertical-tabs
                :tabs="vendorSettingsTabs"
                :currentTab="vendorSettingsTab"
                :wrapper-class="'pt-5 w-96 border-r border-grey-200 pb-10'"
                @onClick="handleVendorSettingsTabChange"
              />
            <div class="py-5 px-6 w-full">
              <div v-if="vendorSettingsTab === 'selfbilling'">
                <div class="flex flex-col w-full mb-6">
                  <div class="flex flex-col mb-6">
                    <label class="text-grey-700 mt-2 mr-2">{{
                      $t('frontend_funnels_your_customers')
                    }}</label>
                    <div class="w-1/2">
                      <text-input
                        v-model="selfbillingLink"
                        class="mt-2"
                        :readonly="true"
                        value="selfbillingLink"
                        :placeholder="$t('frontend_funnels_customer_portal')"
                        :select-all="true"
                      />
                    </div>
                  </div>
                  <div class="flex flex-wrap w-full">
                    <div class="flex w-1/2 mt-4">
                      <div class="mr-2">
                        {{ $t('frontend_funnels_allow_invoice') }}
                      </div>
                      <gd-toggle
                        v-model="form.selfbilling_info.invoice_download"
                        :value="form.selfbilling_info.invoice_download"
                      />
                    </div>
                    <div class="flex w-1/2 mt-4">
                      <div class="mr-2">
                        {{ $t('frontend_funnels_allow_credit_card') }}
                      </div>
                      <gd-toggle
                        v-model="form.selfbilling_info.update_payment_info"
                        :value="form.selfbilling_info.update_payment_info"
                      />
                    </div>
                    <div class="flex w-1/2 mt-4">
                      <div class="mr-2">
                        {{ $t('frontend_funnels_allow_refund') }}
                      </div>
                      <gd-toggle
                        v-model="form.selfbilling_info.refund"
                        :value="form.selfbilling_info.refund"
                      />
                    </div>
                    <div class="flex w-1/2 mt-4">
                      <div class="mr-2">
                        {{ $t('frontend_funnels_allow_cancel') }}
                      </div>
                      <gd-toggle
                        v-model="form.selfbilling_info.cancel"
                        :value="form.selfbilling_info.cancel"
                      />
                    </div>
                  </div>
                </div>
                <hr class="border-grey-100 -mx-4" />
                <div class="flex flex-row-reverse pr-2 pb-4">
                  <gd-button
                    v-if="!$v.form.$invalid"
                    primary
                    size="large"
                    class="text-center mt-6"
                    @click="saveVendorSettings"
                    >{{ $t('frontend_save') }}</gd-button
                  >
                  <gd-button
                    v-else
                    disabled
                    size="large"
                    class="text-center mt-6 cursor-not-allowed"
                    >{{ $t('frontend_save') }}</gd-button
                  >
                </div>
              </div>
              
              <div v-if="vendorSettingsTab === 'facebook_conversion'">
                <div class="flex flex-row justify-end mb-6">
                  <gd-button
                    size="auto"
                    primary
                    class="p-4"
                    @click="createPixel"
                    >+ Add New Pixel</gd-button
                  >
                </div>
                <div
                  v-if="fb_conversion_detail_list.length"
                  id="links-list-view"
                  class="settings-tables-view"
                >
                  <v-client-table
                    ref="linksTable"
                    :data="fb_conversion_detail_list"
                    :columns="tableColumns"
                    :options="tableOptions"
                    class="bg-white text-center rounded border border-grey-300"
                  >
                    <div slot="actions" slot-scope="props">
                      <div class="flex flex-row">
                        <gd-button
                          primary
                          size="icon"
                          class="mr-2"
                          @click="editDetail(props.row.id)"
                        >
                          <em class="fas fa-edit"></em>
                        </gd-button>

                        <gd-button
                          primary
                          size="icon"
                          class="mr-2"
                          @click="sendTestEvent(props.row.id)"
                          :title="$t('frontend_groovesell_send_test_event')"
                        >
                          <em class="fas fa-paper-plane"></em>
                        </gd-button>
                        <gd-button
                          primary
                          size="icon"
                          class="mr-2"
                          @click="deleteDetail(props.row.id)"
                        >
                          <em class="fas fa-trash"></em>
                        </gd-button>
                      </div>
                    </div>
                  </v-client-table>
                </div>
                <div v-else>{{ $t('frontend_no_pixel_added') }}</div>
              </div>
            </div>
          </div>
        </div>

        <gd-modal
          name="add-facebook-conversion-detail-modal"
          :title="$t('frontend_groovesell_facebook_conversion_details')"
        >
          <div class="flex flex-col m-10 mt-6">
            <div class="flex flex-col w-full mb-6">
              <div class="flex flex-col w-1/1 mb-4">
                <label class="text-grey-700 mt-2 mr-2">{{
                  $t('frontend_pixel_name')
                }}</label>

                <text-input
                  v-model="fb_conversion_detail.name"
                  :validator="$v.fb_conversion_detail.name"
                  class="mt-2"
                  value="fb_conversion_detail.name"
                  :placeholder="$t('frontend_pixel_name')"
                />
              </div>

              <div class="flex flex-col w-1/1 mb-4">
                <label class="text-grey-700 mt-2 mr-2">{{
                  $t('frontend_pixel_id')
                }}</label>

                <text-input
                  v-model="fb_conversion_detail.pixel_id"
                  :validator="$v.fb_conversion_detail.pixel_id"
                  class="mt-2"
                  value="fb_conversion_detail.pixel_id"
                  :placeholder="$t('frontend_pixel_id')"
                />
              </div>

              <div class="flex flex-col w-1/1 mb-4">
                <label class="text-grey-700 mt-2 mr-2">{{
                  $t('frontend_access_token')
                }}</label>

                <text-area
                  v-model="fb_conversion_detail.access_token"
                  :validator="$v.fb_conversion_detail.access_token"
                  class="mt-2"
                  value="fb_conversion_detail.access_token"
                  :placeholder="$t('frontend_access_token')"
                />
              </div>

              <div class="w-full flex flex-row mb-4">
                <label class="w-full mt-1 text-grey-700"
                  ><span class="text-red">Note:</span> Please follow the guide
                  <a
                    href="https://developers.facebook.com/docs/marketing-api/conversions-api/get-started"
                    class="text-blue underline underline-offset-1 font-semibold"
                    target="_blank"
                    >here</a
                  >
                  to get your pixel id and your access token</label
                >
              </div>

              <div class="flex justify-end pr-2 pb-4">
                <gd-button
                  v-if="!$v.fb_conversion_detail.$invalid"
                  primary
                  size="large"
                  class="text-center mt-6"
                  @click="saveConversionSettings"
                  >{{ $t('frontend_save') }}</gd-button
                >
                <gd-button
                  v-else
                  disabled
                  size="large"
                  class="text-center mt-6 cursor-not-allowed"
                  >{{ $t('frontend_save') }}</gd-button
                >
              </div>
            </div>
          </div>
        </gd-modal>
      </v-wait>
    </div>
  </div>
</template>

<script>
import Form from 'vform';
import axios from 'axios';
import {
  required,
  minLength,
  maxLength,
  alphaNum,
} from 'vuelidate/lib/validators';
import Loading from 'vue-loading-overlay';
import Swal from 'sweetalert2';
import themeConfig from './theme/themeConfig.js';
import 'vue-loading-overlay/dist/vue-loading.css';
import Notify from '@groovepages/gd-lib/notify';
import VerticalTabs from './components/VerticalTabs';

const touchMap = new WeakMap();

export default {
  middleware: 'auth',
  components: {
    Loading,
    VerticalTabs,
  },
  data() {
    return {
      is_editing: false,
      editing_id: null,
      tableColumns: ['name', 'actions'],
      tableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [],
        filterByColumn: false,
        filterable: false,
        childRowTogglerFirst: false,
        perPage: 10000,
        rowClassCallback() {
          return 'hover:bg-grey-50 funnelRow';
        },
      },
      selfbillingLink: 'https://billing.groovesell.com',
      form: new Form({
        selfbilling_info: {},
      }),
      fb_conversion_detail: {},
      fb_conversion_detail_list: [],
      isLoading: false,
      fullPageLoader: true,
      loaderColor: themeConfig.appPrimaryColor,
      vendorSettingsTab: 'selfbilling',
      itemsPerPage: 5,
      model: {
        name: '',
        type: 1,
      },
      selectedFunnelProducts: [],
      genericUrl: `${window.config.apiUrl.split('/api')[0]}/api`,
    };
  },
  metaInfo() {
    return { title: 'Vendor Settings' };
  },
  validations: {
    form: {},
    fb_conversion_detail: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },

      pixel_id: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(80),
      },

      access_token: {
        required,
        minLength: minLength(3),
      },
    },
  },
  computed: {
    vendorSettingsTabs () {
      const tabs = [
        // {
        //   title: this.$t('groovesell_vendor_settings_payment_gateways'),
        //   value: 'setupGateways',
        // },
        {
          title: this.$t('frontend_facebook_conversion_api_detail'),
          value: 'facebook_conversion',
        }
      ]

      tabs.splice(0, 0, ...[
        {
          title: this.$t('frontend_funnels_self_billing'),
          value: 'selfbilling',
        }
      ])

      return tabs
    },
    menuItems() {
      const menuItems = [];

      menuItems.push({
        text: 'Add Processing Fees',
        callback: () => this.$emit('onManageProducts', this.funnel.id),
      });

      return menuItems;
    },
  },
  async created() {
    this.$wait.start('settings');
    const mainAppUrl = `${window.config.apiUrl.split('/api')[0]}/api`;
    const { data } = await axios.get(`${mainAppUrl}/groovesell/vendor-settings`);
    this.form.selfbilling_info = data.selfbilling_info;

    this.getList();

    this.$wait.end('settings');
  },
  methods: {
    async createPixel() {
      this.fb_conversion_detail = {};
      this.is_editing = false;
      this.$root.$emit('modal-open', 'add-facebook-conversion-detail-modal');
    },

    async editDetail(Id) {
      console.log(Id);
      this.showLoader();
      this.editing_id = Id;
      this.is_editing = true;
      const fbSetting = await axios.post(`${this.genericUrl}/groovesell/conversion-settings`, {
        list: false,
        id: Id,
      });
      this.fb_conversion_detail = fbSetting.data;
      this.$root.$emit('modal-open', 'add-facebook-conversion-detail-modal');
      this.hideLoader();
    },

    async sendTestEvent(Id) {
      Swal.fire({
        title: '',
        text: 'Enter Your Test Event Name',
        input: 'text',
        inputAttributes: {
          autocapitalize: 'off',
        },
        showCancelButton: true,
        confirmButtonText: 'Send',
        showLoaderOnConfirm: true,

        inputValidator: (value) => {
          return !value && 'You need to write something!';
        },

        preConfirm: (eventName) => {
          return axios
            .post(`${this.genericUrl}/groovesell/send-test-event`, {
              event_name: eventName,
              pixel_id: Id,
            })
            .then((response) => {
              if (!response.data.events_received) {
                throw new Error(response.statusText);
              }
            })
            .catch((error) => {
              Swal.showValidationMessage(`Request failed: ${error}`);
            });
        },
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.value) {
          Swal.fire({
            icon: 'success',
            title: '',
            text: 'Test Event Sent Successfully!',
            showConfirmButton: false,
            timer: 1500,
          });
        }
      });
    },

    async deleteDetail(Id) {
      const { value: accept } = await Swal.fire({
        title: this.$t('frontend_sure'),
        text: this.$t('frontend_non_revertable'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('frontend_delete_confirm'),
      });

      if (accept) {
        this.showLoader();
        axios
          .delete(`${this.genericUrl}/groovesell/delete-conversion-settings/${Id}`)
          .then(() => {
            this.getList();
            this.hideLoader();
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },

    handleVendorSettingsTabChange(newTab) {
      this.vendorSettingsTab = newTab;
    },

    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    async saveVendorSettings() {
      const self = this;
      self.showLoader();

      await self.form
        .post(`${this.genericUrl}/groovesell/save-vendor-settings`)
        .then(() => {
          self.hideLoader();
          Notify.success('Successfully saved your settings');
        })
        .catch(() => {
          self.hideLoader();
          Notify.fail(
            'Unable to save your settings. Please refresh this page and try again'
          );
        });
    },

    async saveConversionSettings() {
      // this.$v.$touch();
      const self = this;
      self.showLoader();
      self.fb_conversion_detail.is_editing = this.is_editing;
      self.fb_conversion_detail.id = this.editing_id;

      await axios
        .post(
          `${this.genericUrl}/groovesell/save-conversion-settings`,
          self.fb_conversion_detail
        )
        .then(() => {
          this.getList();
          this.$root.$emit(
            'modal-close',
            'add-facebook-conversion-detail-modal'
          );

          self.hideLoader();
          Notify.success('Successfully saved your settings');
        })
        .catch(() => {
          self.hideLoader();
          Notify.fail(
            'Unable to save your settings. Please refresh this page and try again'
          );
        });
    },

    async getList() {
      const fbSetting = await axios.post(`${this.genericUrl}/groovesell/conversion-settings`, {
        list: true,
      });
      this.fb_conversion_detail_list = fbSetting.data.data;
    },

    openGatewayModal() {
      this.$root.$emit('modal-open', 'payment-gateway-modal');
    },
  },
};
</script>

<style lang="scss">
.settings-tables-view {
  .VueTables--client table {
    overflow: auto !important;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 400;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid #fd6481;
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit {
    display: none;
  }

  .VuePagination__count {
    display: none;
  }

  .expandIcon {
    display: none;
  }

  .funnelRow:hover .expandIcon {
    display: block !important;
  }
}
.scroll-modal {
  max-height: calc(100vh - 80px);
}
</style>
